// PromptComposer — UI shell for drafting a ticket from a brief.
//
// The composer always produces ONE draft from the brief. The previous
// keyword-driven multi-split was misleading (it slotted any 3-line brief
// into a fixed schema/impl/rollback template that had no connection to
// what the user wrote). The first agent in the loop has codebase context
// to decompose internally; users who explicitly want multiple tickets
// from one brief can use the chat concierge — its queue_ticket tool can
// be invoked multiple times in one turn.
//
// What synthesizeTickets does today: pull a title from the first line,
// stitch the rest into a description, run extractAcs to seed 1-3 concrete
// acceptance criteria. The user can edit before queueing.

import { useEffect, useRef, useState } from 'react';
import type { AcceptanceCriterion } from '../lib/types';

export interface DraftTicket {
  id: string;
  title: string;
  description: string;
  ac: AcceptanceCriterion[];
  mode: 'autonomous' | 'assisted';
  agent: string;
  reviewer: string;
  loops: number;
  /** M12: repo this run targets. URL for autonomous (sandbox clones it),
   *  path for assisted (agent edits in place). When both are blank the
   *  platform falls back to its GITHUB_OWNER/REPO + REPO_PATH defaults. */
  repoUrl?: string;
  repoPath?: string;
  /** User-defined spend ceiling in USD. Undefined → no cap. */
  maxBudgetUsd?: number;
}

interface PromptComposerProps {
  onAcceptDrafts: (drafts: DraftTicket[], opts?: { stash?: boolean }) => void;
  /** When true, the composer collapses itself once. The user can still
   *  re-open via the header chevron — this is a one-shot nudge, not a lock.
   *  Used by the parent to auto-collapse on first chat send. */
  forceCollapsed?: boolean;
}

const AGENT_OPTIONS = ['anthropic', 'openai', 'codex-oauth', 'google', 'openrouter'];

export function PromptComposer({ onAcceptDrafts, forceCollapsed }: PromptComposerProps) {
  const [open, setOpen] = useState(true);
  // One-shot auto-collapse: when `forceCollapsed` flips to true, drop the
  // open state once. After that, the user owns the toggle — we don't keep
  // forcing it shut every render, otherwise they couldn't reopen.
  const collapsedRef = useRef(false);
  useEffect(() => {
    if (forceCollapsed && !collapsedRef.current) {
      collapsedRef.current = true;
      setOpen(false);
    }
  }, [forceCollapsed]);
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'autonomous' | 'assisted'>('autonomous');
  const [agent, setAgent] = useState('anthropic');
  const [reviewer, setReviewer] = useState('google');
  const [loops, setLoops] = useState(3);
  const [repoUrl, setRepoUrl] = useState('');
  const [repoPath, setRepoPath] = useState('');
  const [budget, setBudget] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [drafts, setDrafts] = useState<DraftTicket[]>([]);

  const examples = [
    'Add a /users/:id/profile endpoint with avatar resolver and tests',
    'Backfill organization_id on legacy invoice rows, idempotent + chunked',
    'Add a /healthz endpoint that reports DB + redis liveness',
  ];

  // Front-end mirror of services/review-loop/src/repo-url.ts's parseGitHubUrl.
  // Kept in sync so a malformed URL fails at submit-time in the composer
  // rather than as a queued-then-failed run. The server still validates
  // the same way — this is just for immediate operator feedback.
  const repoUrlError =
    mode === 'autonomous' && repoUrl.trim().length > 0 && !isParseableGitHubUrl(repoUrl.trim())
      ? 'expected https://github.com/<owner>/<repo>'
      : null;

  const draft = (): void => {
    if (!text.trim()) return;
    if (repoUrlError) return;
    setDrafting(true);
    setDrafts([]);
    window.setTimeout(() => {
      const parsedBudget = parseBudget(budget);
      const out = synthesizeTickets(text);
      setDrafts(
        out.map((d, i) => ({
          ...d,
          id: nextId(i),
          mode,
          agent,
          reviewer,
          loops,
          ...(repoUrl.trim() ? { repoUrl: repoUrl.trim() } : {}),
          ...(repoPath.trim() ? { repoPath: repoPath.trim() } : {}),
          ...(parsedBudget !== undefined ? { maxBudgetUsd: parsedBudget } : {}),
        })),
      );
      setDrafting(false);
    }, 700);
  };

  const accept = (idx: number, opts?: { stash?: boolean }): void => {
    const d = drafts[idx];
    if (!d) return;
    onAcceptDrafts([d], opts);
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
  };
  const acceptAll = (opts?: { stash?: boolean }): void => {
    onAcceptDrafts(drafts, opts);
    setDrafts([]);
    setText('');
  };
  const reject = (idx: number): void => setDrafts((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className={`composer ${open ? 'open' : 'closed'}`}>
      <div className="composer-head" onClick={() => setOpen((v) => !v)}>
        <span className="composer-chev mono">{open ? '▾' : '▸'}</span>
        <span className="composer-title">Draft a ticket</span>
        <span className="composer-sub mono">
          write a brief — accept queues a real run on the platform
        </span>
        <span className="composer-kbd mono">⌘ ↵</span>
      </div>

      {open && (
        <div className="composer-body">
          <div className="composer-input-row">
            <textarea
              className="composer-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  draft();
                }
              }}
              placeholder={'describe the work — paste a spec, write a 1-liner, or dump a thread.\nclick draft to seed a ticket, edit if needed, then accept to queue a run.'}
              rows={3}
            />
            <div className="composer-side">
              <div className="composer-defaults">
                <ComposerSelect label="mode" value={mode} onChange={(v) => setMode(v as 'autonomous' | 'assisted')} options={['autonomous', 'assisted']} />
                <ComposerSelect label="agent" value={agent} onChange={setAgent} options={AGENT_OPTIONS} />
                <ComposerSelect label="reviewer" value={reviewer} onChange={setReviewer} options={AGENT_OPTIONS.filter((p) => p !== agent)} />
                <ComposerSelect
                  label="loops"
                  value={String(loops)}
                  onChange={(v) => setLoops(parseInt(v, 10))}
                  options={['0', '1', '2', '3', '4', '5']}
                />
              </div>
              <label className="composer-select" style={{ width: '100%' }}>
                <span className="composer-select-label mono">
                  {mode === 'autonomous' ? 'repo URL (sandbox)' : 'repo path (local)'}
                </span>
                {mode === 'autonomous' ? (
                  <>
                    <input
                      className={`mono ${repoUrlError ? 'composer-input-error' : ''}`}
                      type="text"
                      value={repoUrl}
                      onChange={(e) => setRepoUrl(e.target.value)}
                      placeholder="https://github.com/owner/repo · blank → platform default"
                      aria-invalid={repoUrlError !== null}
                    />
                    {repoUrlError && (
                      <span className="composer-input-error-msg mono">{repoUrlError}</span>
                    )}
                  </>
                ) : (
                  <input
                    className="mono"
                    type="text"
                    value={repoPath}
                    onChange={(e) => setRepoPath(e.target.value)}
                    placeholder="/abs/path/to/checkout · blank → platform default"
                  />
                )}
              </label>
              <label className="composer-select" style={{ width: '100%' }}>
                <span className="composer-select-label mono">max budget (USD)</span>
                <input
                  className="mono"
                  type="text"
                  inputMode="decimal"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="e.g. 2 · blank → no cap"
                />
              </label>
              <button
                className="draft-btn"
                onClick={draft}
                disabled={!text.trim() || drafting || repoUrlError !== null}
                title={repoUrlError ? `repo URL: ${repoUrlError}` : undefined}
              >
                {drafting ? 'drafting…' : 'draft ticket →'}
              </button>
            </div>
          </div>

          {!drafting && drafts.length === 0 && !text && (
            <div className="composer-examples">
              <span className="composer-examples-label mono">try:</span>
              {examples.map((ex, i) => (
                <button key={i} className="example-chip mono" onClick={() => setText(ex)}>
                  {ex}
                </button>
              ))}
            </div>
          )}

          {drafting && (
            <div className="composer-drafting mono">
              <span className="composer-spinner" />
              splitting brief into tickets…
            </div>
          )}

          {drafts.length > 0 && (
            <div className="drafts-wrap">
              <div className="drafts-head">
                <span className="mono drafts-head-label">
                  {drafts.length} ticket{drafts.length === 1 ? '' : 's'} drafted · queue now or stash to backlog
                </span>
                <div className="drafts-actions">
                  <button className="drafts-action" onClick={() => setDrafts([])}>discard all</button>
                  <button className="drafts-action" onClick={() => acceptAll({ stash: true })}>stash all</button>
                  <button className="drafts-action primary" onClick={() => acceptAll()}>queue all</button>
                </div>
              </div>
              <div className="drafts-grid">
                {drafts.map((d, i) => (
                  <DraftCard
                    key={d.id}
                    draft={d}
                    onAccept={() => accept(i)}
                    onStash={() => accept(i, { stash: true })}
                    onReject={() => reject(i)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ComposerSelect(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="composer-select">
      <span className="composer-select-label mono">{props.label}</span>
      <select value={props.value} onChange={(e) => props.onChange(e.target.value)}>
        {props.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

function DraftCard(props: {
  draft: DraftTicket;
  onAccept: () => void;
  onStash: () => void;
  onReject: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(props.draft.title);
  const [desc, setDesc] = useState(props.draft.description);
  const [acText, setAcText] = useState(props.draft.ac.map((a) => a.text).join('\n'));

  useEffect(() => {
    setTitle(props.draft.title);
    setDesc(props.draft.description);
    setAcText(props.draft.ac.map((a) => a.text).join('\n'));
  }, [props.draft, editing]);

  if (editing) {
    return (
      <div className="draft-card editing">
        <div className="draft-edit-row">
          <span className="ticket-id">[{props.draft.id}]</span>
        </div>
        <input className="draft-edit-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea
          className="draft-edit-textarea"
          rows={2}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="description"
        />
        <textarea
          className="draft-edit-textarea mono-textarea"
          rows={3}
          value={acText}
          onChange={(e) => setAcText(e.target.value)}
          placeholder="acceptance criteria, one per line"
        />
        <div className="draft-actions">
          <button className="draft-btn-sec" onClick={() => setEditing(false)}>cancel</button>
          <button className="draft-btn-pri" onClick={() => {
            // Mutate the draft in place — DraftTicket is owned by parent, but
            // for v1 the only way to surface changes is to call accept after
            // edit. We just close the editor.
            setEditing(false);
          }}>save</button>
        </div>
      </div>
    );
  }

  return (
    <div className="draft-card">
      <div className="draft-head">
        <span className="ticket-id">[{props.draft.id}]</span>
        <span className={`mode-badge mode-${props.draft.mode}`}>{props.draft.mode}</span>
        <span className="provider-chip">
          <span>{props.draft.agent}</span>
          <span className="provider-arrow">→</span>
          <span>{props.draft.reviewer}</span>
        </span>
        <span className="loops-chip mono">loop ≤{props.draft.loops}</span>
      </div>
      <div className="draft-title">{props.draft.title}</div>
      {props.draft.description && (
        <div className="draft-desc">{props.draft.description}</div>
      )}
      {props.draft.ac.length > 0 && (
        <ul className="draft-ac">
          {props.draft.ac.map((a, k) => (
            <li key={k}>
              <span className="ac-box">○</span>
              <span>{a.text}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="draft-actions">
        <button className="draft-btn-sec" onClick={props.onReject}>discard</button>
        <button className="draft-btn-sec" onClick={() => setEditing(true)}>edit</button>
        <button className="draft-btn-sec" onClick={props.onStash}>stash</button>
        <button className="draft-btn-pri" onClick={props.onAccept}>queue run</button>
      </div>
    </div>
  );
}

function parseBudget(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed.replace(/^\$/, ''));
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

// Same regex the server's services/review-loop/src/repo-url.ts uses. Both
// sides must agree or the composer accepts a URL the orchestrator later
// rejects — keep this comment + the file path here so anyone touching the
// pattern updates the other side too.
export function isParseableGitHubUrl(url: string): boolean {
  return /^https?:\/\/github\.com\/[^/]+\/[^/?#]+?(?:\.git)?\/?$/i.test(url);
}

function nextId(offset = 0): string {
  // Stable client-side ticket id — collision-free across composer sessions.
  // Format: UI-YYYYMMDD-<6-char-base36>-<offset>. The platform doesn't
  // interpret the value; runs.ticket_id is just a label.
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const rand = Math.random().toString(36).slice(2, 8);
  return `UI-${ymd}-${rand}-${offset}`;
}

interface SynthDraft {
  title: string;
  description: string;
  ac: AcceptanceCriterion[];
}

function synthesizeTickets(brief: string): SynthDraft[] {
  // Always produce ONE ticket from the brief. The previous keyword-driven
  // multi-split ("phases", "split into", or 3+ lines → fixed schema/impl/
  // rollback template) cut up briefs in ways that had no relation to what
  // the user actually wrote. The first agent in the loop has codebase
  // context to decompose internally; if the user wants explicit multi-ticket
  // breakdown they can ask the chat concierge.
  const lines = brief.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const title = titleize(lines[0] ?? brief);
  const description = lines.length > 1 ? lines.slice(1).join(' ') : brief;
  return [
    {
      title,
      description,
      ac: extractAcs(brief),
    },
  ];
}

function titleize(s: string): string {
  let t = s.replace(/^[-*•]\s*/, '').trim();
  if (t.length > 90) t = t.slice(0, 87) + '…';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function extractAcs(brief: string): AcceptanceCriterion[] {
  const lower = brief.toLowerCase();
  const acs: string[] = [];

  // Topic-keyed: pick up specific verticals first.
  if (lower.includes('endpoint') || lower.includes('api') || /\b(get|post|put|patch|delete)\s+\//i.test(brief)) {
    acs.push('Endpoint returns 200 with the documented response shape on valid input');
    acs.push('Returns a 4xx with a structured error on invalid input');
  }
  if (lower.includes('rate limit')) acs.push('Rate limit enforced with sane defaults and a clear 429 response');
  if (lower.includes('idempot')) acs.push('Idempotent — safe to retry without side-effects');
  if (lower.includes('migration') || lower.includes('backfill')) {
    acs.push('Migration is reversible (up + down) and chunked for large tables');
  }
  if (lower.includes('webhook')) acs.push('Webhook signature is verified before any state change');
  if (lower.includes('cache') || lower.includes('cach')) acs.push('Cache invalidates correctly on the documented write paths');
  if (lower.includes('auth') || lower.includes('login') || lower.includes('token')) {
    acs.push('Unauthenticated requests are rejected with 401');
  }

  // Always require evidence that something works — every ticket gets a
  // testing-related AC unless the user explicitly opted in to one above.
  if (!acs.some((a) => /test|coverage/i.test(a))) {
    acs.push('Unit and integration tests cover the new behavior');
  }

  // Fall-through: distill the brief into a single observable behavior
  // when nothing keyword-triggered above. This is still weak but at
  // least concrete enough that the agent has something to verify.
  if (acs.length <= 1) {
    const firstClause = brief.split(/[.!?\n]/)[0]?.trim() ?? brief.slice(0, 80);
    if (firstClause) {
      const subject = firstClause.length > 90 ? firstClause.slice(0, 87) + '…' : firstClause;
      acs.unshift(`Behavior described in the brief works end-to-end: "${subject}"`);
    }
  }

  return acs.slice(0, 3).map((t) => ({ text: t, done: false }));
}
